import { getFormValues, InjectedFormProps, reduxForm } from "redux-form";
import { find, filter, map, size } from "lodash";
import { useMemo, useEffect } from "react";
import moment from "moment";

import { Button, ItemSelectionWrapper, Separator, TextDisplay, Wrapper } from "src/styled";
import {
  dayDisabledMatcher,
  seasonalityAndBlackoutDateRangeMatcher,
} from "src/utils/calendarDisabled";
import { DayOfWeekSetting, SchedulerWizardFormValues, VendorSettings } from "src/interfaces";
import { DisabledDays } from "src/interfaces/schedulerWizard";
import {
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
} from "src/constants/weekDays";
import { setDisabledDays } from "src/redux/reducers";
import { SUNDAY_FIRST, WEEKDAYS } from "src/constants";
import { translate } from "src/services";
import { useAppDispatch } from "src/redux/store";
import { useAppSelector } from "src/redux/store";
import { WIZARD_FORM_NAME } from "./AddressForm";
import ItemPickupDateRow from "./ItemPickupDateRow";
// import PickupScheduleLegend from "./PickupScheduleLegend";

interface PropsWithoutReduxForm {
  goToPreviousStep: () => void;
}

type Props = PropsWithoutReduxForm &
  InjectedFormProps<SchedulerWizardFormValues, PropsWithoutReduxForm>;

const ItemsPickupDateForm = ({ handleSubmit, goToPreviousStep }: Props) => {
  const dispatch = useAppDispatch();

  const citySettings = useAppSelector((state) => state.citySettings.settings);
  const addressValidation = useAppSelector((state) => state.bulkyItemsScheduler.addressValidation);
  const pickupsInZone = useAppSelector((state) => state.bulkyItemsScheduler.pickupsInZone);

  const customMaxFutureSearchableMonths =
    citySettings?.vendorSettings?.maxPickupSchedulePlusMonthsOffset;

  const thisFormValues = useAppSelector(
    getFormValues(WIZARD_FORM_NAME)
  ) as SchedulerWizardFormValues;

  const newDisabledDays: any = useMemo(() => [], []);

  const itemsToDisplay = useMemo(
    () =>
      map(Object.keys(thisFormValues?.items || {}), (item, index) => {
        const currentItem = thisFormValues?.items[item];
        const { bulkyCategoryTypeId } = currentItem;

        const category = find(
          citySettings?.categories,
          (c) => c.bulkyCategoryTypeId === bulkyCategoryTypeId
        );

        const disabledDays: DisabledDays[] = [];

        //  blackout dates
        if (category && category.bulkySchedulerBlackoutSettings) {
          const filteredSeasonality = filter(
            category.bulkySchedulerBlackoutSettings,
            (s) => !!s.isEnabled
          );

          const isBlackout = true;
          !!size(filteredSeasonality) &&
            filteredSeasonality.forEach((seasonality) => {
              disabledDays.push({
                bulkyCategoryTypeId,
                date: seasonalityAndBlackoutDateRangeMatcher(
                  [seasonality],
                  isBlackout,
                  customMaxFutureSearchableMonths
                ),
              });
            });
        }

        // seasonality dates
        if (category && category.bulkySchedulerSeasonalPickUpSettings) {
          const filteredSeasonality = filter(
            category.bulkySchedulerSeasonalPickUpSettings,
            (s) => !!s.isEnabled
          );

          const isBlackout = false;
          !!size(filteredSeasonality) &&
            disabledDays.push({
              bulkyCategoryTypeId,
              date: seasonalityAndBlackoutDateRangeMatcher(
                filteredSeasonality,
                isBlackout,
                customMaxFutureSearchableMonths
              ),
            });
        }

        // reoccurring service days
        if (addressValidation && addressValidation.restrictedAvailabilityDays) {
          const restrictedAvailabilityDaysById = addressValidation.restrictedAvailabilityDays.map(
            (day) => day.id
          );

          const restrictedAvailabilityDays: number[] = [];

          WEEKDAYS?.forEach((day) => {
            if (!restrictedAvailabilityDaysById?.includes(day.id)) {
              restrictedAvailabilityDays.push(day.id === SUNDAY ? SUNDAY_FIRST : day.id);
            }
          });

          !!restrictedAvailabilityDays.length &&
            disabledDays.push({
              bulkyCategoryTypeId,
              daysOfWeek: restrictedAvailabilityDays,
            });
        }

        // category availability settings (custom and default)
        if (category && citySettings?.vendorSettings) {
          const newDate = dayDisabledMatcher(
            citySettings,
            addressValidation,
            thisFormValues?.items[item],
            thisFormValues,
            pickupsInZone
          );

          disabledDays.push({
            bulkyCategoryTypeId,
            date: newDate,
          });
        }

        // same and next day settings
        const { allowFutureDayPickupRequest, futureDayPickupScheduleDaysOffset } =
          citySettings?.vendorSettings || ({} as VendorSettings);

        if (
          allowFutureDayPickupRequest &&
          futureDayPickupScheduleDaysOffset &&
          futureDayPickupScheduleDaysOffset >= 0
        ) {
          for (let i = 0; i < futureDayPickupScheduleDaysOffset; i++) {
            disabledDays.push({
              bulkyCategoryTypeId,
              date: moment().add(i, "day").toDate(),
            });
          }
        }

        // disable days of week when maxPickups is 0
        if (category?.dayOfWeekList) {
          const daysOfWeek: number[] = [];

          const categoriesWithZoneId = category?.dayOfWeekList?.filter(
            (dayOfWeek: DayOfWeekSetting) =>
              addressValidation?.zoneIds?.some((zoneId) => dayOfWeek?.zoneIds?.includes(zoneId))
          );

          const categoriesWithZoneIdFiltered = Object.values(
            categoriesWithZoneId?.reduce((acc, curr) => {
              if (!acc[curr.dayOfWeek] || curr.maxPickups > acc[curr.dayOfWeek].maxPickups) {
                acc[curr.dayOfWeek] = curr;
              }
              return acc;
            }, {} as DayOfWeekSetting as any)
          ) as DayOfWeekSetting[];

          const categoryWithoutZoneId = category?.dayOfWeekList?.filter(
            (dayOfWeek: DayOfWeekSetting) => !dayOfWeek.zoneIds?.length
          );

          const currentCategory = !!categoriesWithZoneIdFiltered?.length
            ? categoriesWithZoneIdFiltered
            : categoryWithoutZoneId;

          currentCategory?.forEach((day) => {
            if (day.maxPickups === 0) {
              daysOfWeek.push(day.dayOfWeek === SUNDAY ? SUNDAY_FIRST : day.dayOfWeek);
            }
          });

          !!daysOfWeek.length &&
            disabledDays.push({
              bulkyCategoryTypeId,
              daysOfWeek,
            });
        }

        // disable days of week when restrictPickup is true
        const {
          restrictMondayPickup,
          restrictTuesdayPickup,
          restrictWednesdayPickup,
          restrictThursdayPickup,
          restrictFridayPickup,
          restrictSaturdayPickup,
          restrictSundayPickup,
        } = citySettings?.vendorSettings || ({} as VendorSettings);

        if (
          restrictMondayPickup ||
          restrictTuesdayPickup ||
          restrictWednesdayPickup ||
          restrictThursdayPickup ||
          restrictFridayPickup ||
          restrictSaturdayPickup ||
          restrictSundayPickup
        ) {
          const restrictedDays: number[] = [];

          restrictMondayPickup && restrictedDays.push(MONDAY);
          restrictTuesdayPickup && restrictedDays.push(TUESDAY);
          restrictWednesdayPickup && restrictedDays.push(WEDNESDAY);
          restrictThursdayPickup && restrictedDays.push(THURSDAY);
          restrictFridayPickup && restrictedDays.push(FRIDAY);
          restrictSaturdayPickup && restrictedDays.push(SATURDAY);
          restrictSundayPickup && restrictedDays.push(SUNDAY_FIRST);

          disabledDays.push({
            bulkyCategoryTypeId,
            daysOfWeek: restrictedDays,
          });
        }

        // final push with all disabled days
        newDisabledDays.push(disabledDays);

        return (
          <ItemPickupDateRow
            item={thisFormValues?.items[item]}
            key={item}
            category={category}
            index={index}
          />
        );
      }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    setDisabledDays(newDisabledDays)(dispatch);
  }, [thisFormValues.itemsMultiselect, dispatch, newDisabledDays]);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Separator height={50} />
      <TextDisplay margin="no no medium" block>
        <b>{translate("wizardPage.pickupDate")}</b>
      </TextDisplay>
      <Separator height={30} />
      <ItemSelectionWrapper>{itemsToDisplay}</ItemSelectionWrapper>
      <Separator height={10} />

      {/* TODO add PickupScheduleLegend back when correct information is provided by the API */}
      {/* <PickupScheduleLegend />
      <Separator height={50} /> */}

      <Wrapper display="flex" justifyContent="space-between">
        <Button
          type="button"
          color="primary"
          size="small"
          borderRadius="100px 0 0 100px"
          margin="no xSmall no no"
          line
          onClick={goToPreviousStep}
        >
          {translate("common.previousStep")}
        </Button>
        <Button
          type="submit"
          color="primary"
          size="medium"
          borderRadius="0 100px 100px 0"
          disabled={!size(thisFormValues?.items || {})}
        >
          {translate("common.nextStep")}
        </Button>
      </Wrapper>
      <Separator height={100} />
    </form>
  );
};

export default reduxForm<SchedulerWizardFormValues, PropsWithoutReduxForm>({
  form: WIZARD_FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(ItemsPickupDateForm);
